/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TypeScript/React version of DocState
 * Based on the original JSX version in /lego/page/browser/doc-state/DocState.jsx
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { FsiLifecycleState } from '../../constants/fsi'

interface FileSystemItem {
  id: string | number
  name?: string
  type?: string
  valid?: boolean
  systemFolderType?: string
  acl?: any
  states?: string[]
  // File lifecycle properties
  obsolete?: boolean
  rejected?: boolean
  expired?: boolean
  isBlindedDocument?: boolean
  [key: string]: any // Allow additional properties
}

interface DocStateProps {
  fsi: FileSystemItem
  className?: string
}

/**
 * Gets the states for a file system item
 * Based on the original file.getFsiStates service method in app/service/file.service.js
 */
const getFsiStates = (fsi: FileSystemItem): string[] => {
  if (!fsi) {
    return []
  }

  const states: string[] = []

  // Check lifecycle state
  const lifecycleState = (() => {
    if (fsi.obsolete) {
      return FsiLifecycleState.OBSOLETE
    } else if (fsi.rejected) {
      return FsiLifecycleState.REJECTED
    } else if (fsi.expired) {
      return FsiLifecycleState.EXPIRED
    }
    return null
  })()

  if (lifecycleState) {
    states.push(`STATE_${lifecycleState}`)
  }

  // Check blinding status
  if (fsi.isBlindedDocument === false) {
    states.push('BLINDING_STATUS_UNBLINDED')
  }

  // Add any additional states that might be present on the fsi object
  if (fsi.states && Array.isArray(fsi.states)) {
    states.push(...fsi.states)
  }

  return states
}

const DocState: React.FC<DocStateProps> = ({ fsi, className = '' }) => {
  const { t } = useTranslation()

  const states = getFsiStates(fsi)

  if (states.length === 0) {
    return null
  }

  return (
    <div className={`inlist-status d-inline-block ${className}`}>
      {states.map((state, index) => (
        <b key={index}>[{t(state).toUpperCase()}]</b>
      ))}
    </div>
  )
}

export default DocState
